import React, { useState, useCallback, useEffect } from 'react'
import { APIWeb } from '@/api'
import { Toast, Loading } from 'react-vant'
import CustomButton from '@/components/CustomButton'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Ripple from '@/components/Ripple'
import { useMerchantConfig } from '@/hooks'


export default React.memo(function ApplyPhysicalCard() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    // 使用商家配置 hook
    const { merchantConfig, loading: loadingConfig } = useMerchantConfig()
    // 邮寄地址
    const [shippingAddress, setShippingAddress] = useState('')
    // 邮寄地址详情
    const [shippingAddressInfo, setShippingAddressInfo] = useState<ShippingAddress | null>(null)
    // 加载状态
    const [loadingAddress, setLoadingAddress] = useState(false)

    // 获取邮寄地址
    const fetchShippingAddress = useCallback(async () => {
        try {
            setLoadingAddress(true)
            const response = await APIWeb.getShippingAddress()

            // 判断响应是直接的数组还是包含 data 的对象
            let addressList: ShippingAddress[] = []

            if (Array.isArray(response)) {
                addressList = response
            } else if (response && response.data && Array.isArray(response.data)) {
                addressList = response.data
            }

            // 从数组中取第一条数据
            if (addressList.length > 0) {
                const firstAddress = addressList[0]
                if (firstAddress) {
                    setShippingAddressInfo(firstAddress)
                    setShippingAddress(firstAddress.full_address || 'filled')
                } else {
                    setShippingAddressInfo(null)
                }
            } else {
                setShippingAddressInfo(null)
            }
        } catch (error) {
            console.error('获取邮寄地址失败:', error)
            setShippingAddressInfo(null)
        } finally {
            setLoadingAddress(false)
        }
    }, [])

    // 页面加载时刷新邮寄地址
    useEffect(() => {
        fetchShippingAddress()
    }, [fetchShippingAddress])

    // 处理确认支付
    const handleConfirmPayment = useCallback(() => {
        // 校验地址
        if (!shippingAddress.trim() || !shippingAddressInfo?.id) {
            Toast.info({
                message: t('请填写邮寄地址'),
                position: 'middle'
            })
            return
        }

        // 跳转到统一的申请卡片页面（实体卡模式），携带邮寄地址 ID
        navigate(`/card/apply-card?type=physical&addressId=${shippingAddressInfo.id}`)
    }, [shippingAddress, shippingAddressInfo, t, navigate])

    // 卡片费用（从商家配置获取）
    const cardFee = merchantConfig?.physical_card_fee || 0 // USD

    return (
        <PageLayout>
            <Header leftChildren={<Back />} centerChildren={<span className="text-3.75 font-medium text-black">{t('申请卡订单')}</span>} />
            <div className="px-6 pt-6">
                {/* 卡片预览 */}
                <div className="mb-6 flex items-center justify-center">
                    <img
                        className="w-80% rounded-3xl"
                        src="/images/card/wpay-physical.png"
                    />
                </div>

                {/* 账单地址 */}
                {/* <div className="mb-4">
                    <div className="text-[#808080] text-sm">{t('账单地址')}</div>
                    <Ripple className="rounded-2xl">
                        <div className="bg-white rounded-2xl py-3 flex justify-between">
                            <div className='leading-6 text-sm'>
                                <div>1 Infinite loop cupertion CA 95014</div>
                                <div>California</div>
                                <div>United States</div>
                            </div>
                            <div className="ml-2">
                                <img className='w-4' src="/icon/edit-address.svg" alt="edit" />
                            </div>
                        </div>
                    </Ripple>
                </div> */}

                {/* 邮寄地址 */}
                <div className="mb-6">
                    <div className="text-[#808080] text-sm mb-2">{t('邮寄地址')}</div>
                    {loadingAddress ? (
                        <div className="flex items-center justify-center py-6">
                            <Loading type='ball' />
                        </div>
                    ) : shippingAddressInfo ? (
                        // 显示邮寄地址详情
                        <Ripple className="rounded-2xl">
                            <div
                                className="bg-white rounded-2xl py-4 pr-4 flex justify-between cursor-pointer"
                            >
                                <div className='leading-6 text-sm flex-1'>
                                    <div className="font-medium mb-1">{shippingAddressInfo.full_name}</div>
                                    <div className="text-[#808080]">{shippingAddressInfo.full_phone}</div>
                                    <div className="text-[#808080] mt-2">{shippingAddressInfo.address_line1}</div>
                                    {shippingAddressInfo.address_line2 && (
                                        <div className="text-[#808080]">{shippingAddressInfo.address_line2}</div>
                                    )}
                                    <div className="text-[#808080]">
                                        {shippingAddressInfo.city}, {shippingAddressInfo.province} {shippingAddressInfo.postal_code}
                                    </div>
                                    <div className="text-[#808080]">{shippingAddressInfo.country}</div>
                                </div>
                            </div>
                        </Ripple>
                    ) : (
                        // 显示添加邮寄地址按钮
                        <div
                            onClick={() => navigate('/card/add-shipping-address')}
                            className="bg-white py-3 px-4 flex items-center justify-between border-[#000000]/10 border-t-solid border-b-solid border cursor-pointer"
                        >
                            <div>{t('添加邮寄地址')}</div>
                            <Ripple className="rounded-full ml-2">
                                <div className="border-[var(--primary-color)] border-solid border rounded-full p-2">
                                    <img className='w-4 block' src="/icon/blue-add.svg" alt="add" />
                                </div>
                            </Ripple>
                        </div>
                    )}
                </div>

                {/* 支付详情卡片 */}
                <div className="text-[#808080] text-sm mb-2">{t('支付详情')}</div>
                <div className="bg-white rounded-2xl py-4 mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-[#7E7E7E] text-3">{t('类型')}</span>
                        <span className="text-[#7E7E7E] text-3.25">{t('实体卡')}</span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-[#7E7E7E] text-3">{t('卡片')}</span>
                        <span className="text-[#7E7E7E] text-3.25">{cardFee || '-'} USD</span>
                    </div>
                    <div className='border-b border-b-solid border-[#000000]/10 my-3'></div>
                    <div className="flex justify-between items-center">
                        <span className="text-3 text-[#7E7E7E]">{t('总计支付')}</span>
                        <span className="text-3.75 text-black font-bold">{cardFee || '-'} USD</span>
                    </div>
                </div>

                {/* 底部按钮区域 */}
                <div className="mt-6 mb-10">
                    <CustomButton
                        onClick={handleConfirmPayment}
                        disabled={loadingConfig || !cardFee}
                    >
                        {t('支付')}
                    </CustomButton>
                </div>
            </div>
        </PageLayout>
    )
})
